import { Vehicle, VehicleRequest } from '../types';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API_BASE_URL = `${BACKEND_URL}/api`;

class VehicleAPI {
  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Get all vehicles for the current driver
  async getMyVehicles(): Promise<{ success: boolean; vehicles: Vehicle[] }> {
    const response = await fetch(`${API_BASE_URL}/vehicles/my-vehicles`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch vehicles');
    }

    return response.json();
  }

  // Get vehicle by ID
  async getVehicle(id: number): Promise<{ success: boolean; vehicle: Vehicle }> {
    const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch vehicle');
    }

    return response.json();
  }

  // Add a new vehicle
  async addVehicle(
    vehicleData: VehicleRequest,
    picture?: File
  ): Promise<{ success: boolean; vehicle: Vehicle; message: string }> {
    const formData = new FormData();

    // Add vehicle data
    Object.entries(vehicleData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Add picture if provided
    if (picture) {
      formData.append('picture', picture);
    }

    const response = await fetch(`${API_BASE_URL}/vehicles`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add vehicle');
    }

    return response.json();
  }

  // Update vehicle
  async updateVehicle(
    id: number,
    vehicleData: Partial<VehicleRequest>,
    picture?: File
  ): Promise<{ success: boolean; vehicle: Vehicle; message: string }> {
    const formData = new FormData();

    // Add vehicle data
    Object.entries(vehicleData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    // Add picture if provided
    if (picture) {
      formData.append('picture', picture);
    }

    const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeader(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update vehicle');
    }

    return response.json();
  }

  // Delete vehicle
  async deleteVehicle(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete vehicle');
    }

    return response.json();
  }

  // Get vehicle picture URL
  getVehiclePictureUrl(id: number): string {
    return `${API_BASE_URL}/vehicles/${id}/picture`;
  }

  // Admin methods

  // Get all vehicles (admin only)
  async getAllVehicles(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    vehicles: Vehicle[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const response = await fetch(
      `${API_BASE_URL}/vehicles/admin/all?${searchParams.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch all vehicles');
    }

    return response.json();
  }

  // Get vehicle statistics (admin only)
  async getVehicleStats(): Promise<{
    success: boolean;
    stats: {
      total_vehicles: number;
      vehicles_by_make: Array<{ make: string; count: number }>;
    };
  }> {
    const response = await fetch(`${API_BASE_URL}/vehicles/admin/stats`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch vehicle statistics');
    }

    return response.json();
  }
}

export const vehicleAPI = new VehicleAPI();
